<!-- 商家端 - 财务管理 -->
<template>
  <el-alert
    class="mb-12px"
    type="info"
    :closable="false"
    show-icon
    title="外发应付供方（预估 · 非结算）"
  >
    <template #default>
      查看本店对外发供方的应付预估与毛利差（不扣钱包；货款线下结）。
      <el-button link type="primary" @click="$router.push('/store/finance/supplier-settle-preview')">
        供方预估
      </el-button>
      ·
      <el-button link type="primary" @click="$router.push('/store/finance/supplier-settle')">
        外发结算
      </el-button>
      <template v-if="platformInvoice">
        ·
        <el-button link type="primary" @click="$router.push('/store/finance/invoice')">
          发票管理
        </el-button>
      </template>
    </template>
  </el-alert>

  <!-- 钱包概览 -->
  <ContentWrap title="钱包概览">
    <el-row :gutter="20">
      <el-col :span="6">
        <el-statistic title="可提现余额" :value="fenToYuan(wallet.availableBalance)" prefix="¥" :precision="2" />
      </el-col>
      <el-col :span="6">
        <el-statistic title="冻结金额" :value="fenToYuan(wallet.frozenBalance)" prefix="¥" :precision="2" />
      </el-col>
      <el-col :span="6">
        <el-statistic title="累计收入" :value="fenToYuan(wallet.totalIncome)" prefix="¥" :precision="2" />
      </el-col>
      <el-col :span="6">
        <el-button type="primary" size="large" @click="openWithdrawDialog">申请提现</el-button>
      </el-col>
    </el-row>
  </ContentWrap>

  <!-- 账单 -->
  <ContentWrap title="账单明细" class="mt-16px">
    <el-form :inline="true" :model="billParams" class="-mb-15px">
      <el-form-item label="账单类型">
        <el-select v-model="billParams.bizType" class="!w-160px" clearable placeholder="全部">
          <el-option label="订单收入" :value="1" />
          <el-option label="退款支出" :value="2" />
          <el-option label="提现" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="getBillList"><Icon class="mr-5px" icon="ep:search" />搜索</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="billLoading" :data="billList" stripe>
      <el-table-column label="账单号" min-width="180" prop="billNo" />
      <el-table-column label="类型" width="100">
        <template #default="{ row }">{{ bizTypeLabel(row.bizType) }}</template>
      </el-table-column>
      <el-table-column label="金额" width="120">
        <template #default="{ row }">
          <span :class="row.amount > 0 ? 'text-green-600' : 'text-red-500'">
            {{ row.amount > 0 ? '+' : '' }}{{ fenToYuan(row.amount) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="说明" min-width="150" prop="title" show-overflow-tooltip />
      <el-table-column label="时间" width="170" prop="createTime" :formatter="dateFormatter" />
    </el-table>
    <Pagination v-model:limit="billParams.pageSize" v-model:page="billParams.pageNo" :total="billTotal" @pagination="getBillList" />
  </ContentWrap>

  <!-- 提现记录 -->
  <ContentWrap title="提现记录" class="mt-16px">
    <el-table v-loading="withdrawLoading" :data="withdrawList" stripe>
      <el-table-column label="提现单号" min-width="180" prop="withdrawNo" />
      <el-table-column label="提现金额" width="120">
        <template #default="{ row }">{{ fenToYuan(row.amount) }} 元</template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="row.status === 10 ? 'success' : row.status === 0 ? 'warning' : 'info'">
            {{ withdrawStatusLabel(row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="申请时间" width="170" prop="createTime" :formatter="dateFormatter" />
    </el-table>
    <Pagination v-model:limit="withdrawParams.pageSize" v-model:page="withdrawParams.pageNo" :total="withdrawTotal" @pagination="getWithdrawList" />
  </ContentWrap>

  <!-- 提现弹窗 -->
  <el-dialog v-model="withdrawDialogVisible" title="申请提现" width="450px">
    <el-form ref="withdrawFormRef" :model="withdrawForm" label-width="100px" :rules="{ amount: [{ required: true, message: '请输入提现金额' }] }">
      <el-form-item label="可提现余额">
        <span class="text-18px font-bold">¥{{ fenToYuan(wallet.availableBalance) }}</span>
      </el-form-item>
      <el-form-item label="提现金额" prop="amount">
        <el-input-number v-model="withdrawForm.amount" :min="0.01" :max="fenToYuan(wallet.availableBalance)" :precision="2" class="!w-240px" placeholder="请输入提现金额" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="withdrawDialogVisible = false">取消</el-button>
      <el-button type="primary" @click="submitWithdraw">确认提现</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import * as MerchantFinanceApi from '@/api/merchant/finance'
import { fenToYuan } from '@/utils'
import { dateFormatter } from '@/utils/formatTime'
import { getMerchantSession } from '@/views/merchant/registry/merchant-session'
import { isPlatformMallBizMode } from '@/views/merchant/registry/mall-biz-mode'

defineOptions({ name: 'MerchantFinance' })

const message = useMessage()
const platformInvoice = computed(() => isPlatformMallBizMode(getMerchantSession()?.mallBizMode))

const wallet = reactive({ availableBalance: 0, frozenBalance: 0, totalIncome: 0 })

// 账单
const billLoading = ref(false)
const billList = ref([])
const billTotal = ref(0)
const billParams = reactive({ pageNo: 1, pageSize: 10, bizType: undefined })

// 提现
const withdrawLoading = ref(false)
const withdrawList = ref([])
const withdrawTotal = ref(0)
const withdrawParams = reactive({ pageNo: 1, pageSize: 10 })
const withdrawDialogVisible = ref(false)
const withdrawForm = reactive({ amount: 0 })

const bizTypeMap: Record<number, string> = { 1: '订单收入', 2: '退款支出', 3: '提现' }
const withdrawStatusMap: Record<number, string> = { 0: '待审核', 10: '已到账', 20: '已拒绝' }
const bizTypeLabel = (t: number) => bizTypeMap[t] || '其他'
const withdrawStatusLabel = (s: number) => withdrawStatusMap[s] || '未知'

const getWallet = async () => {
  const data = await MerchantFinanceApi.getWallet()
  Object.assign(wallet, data)
}

const getBillList = async () => {
  billLoading.value = true
  try {
    const data = await MerchantFinanceApi.getBillPage(billParams)
    billList.value = data.list
    billTotal.value = data.total
  } finally { billLoading.value = false }
}

const getWithdrawList = async () => {
  withdrawLoading.value = true
  try {
    const data = await MerchantFinanceApi.getWithdrawPage(withdrawParams)
    withdrawList.value = data.list
    withdrawTotal.value = data.total
  } finally { withdrawLoading.value = false }
}

const openWithdrawDialog = () => {
  withdrawForm.amount = 0
  withdrawDialogVisible.value = true
}

const submitWithdraw = async () => {
  if (withdrawForm.amount <= 0) { message.warning('请输入有效的提现金额'); return }
  if (withdrawForm.amount > fenToYuan(wallet.availableBalance)) { message.warning('提现金额不能超过可提现余额'); return }
  await MerchantFinanceApi.applyWithdraw({ amount: withdrawForm.amount * 100 })
  message.success('提现申请已提交')
  withdrawDialogVisible.value = false
  getWallet()
  getWithdrawList()
}

onMounted(() => {
  getWallet()
  getBillList()
  getWithdrawList()
})
</script>
